import { useState } from 'react';
import { encodeEstimate, buildShareUrl } from '../../utils/shareEstimate';

interface ChatShareButtonProps {
  userMessage: string;
  aiResponse: string;
  onShared: () => void;
}

export function ChatShareButton({ userMessage, aiResponse, onShared }: ChatShareButtonProps) {
  const [isCopying, setIsCopying] = useState(false);

  const handleShare = async () => {
    try {
      setIsCopying(true);
      const encoded = encodeEstimate(userMessage, aiResponse);
      if (!encoded) return;

      const url = buildShareUrl(encoded);
      await navigator.clipboard.writeText(url);
      onShared();
    } catch {
      // Silently fail if clipboard is unavailable (insecure context, old browser, etc.)
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={isCopying}
      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded transition-all"
      style={{
        backgroundColor: 'transparent',
        border: '1px solid var(--outline-variant)',
        color: 'var(--text-tertiary)',
        cursor: isCopying ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--surface-container-high)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      aria-label="Share this estimate"
    >
      <span className="material-symbols-outlined text-[16px]">share</span>
      <span>Share</span>
    </button>
  );
}
